import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Search, RefreshCw } from 'lucide-react';

import SubscriptionHighlight from './SubscriptionHighlight';
import SubscriptionTable from './SubscriptionTable';
import SubscriptionInsights from './SubscriptionInsights';
import { fetchSubscriptionSummary, selectSubscriptionRows } from '../../store/slices/subscriptionSlice';

const PAGE_SIZE = 10;

const BOARD_TABS = [
  { key: 'mainboard', label: 'Mainboard IPOs' },
  { key: 'sme', label: 'SME IPOs' },
];

const formatRefreshedAt = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

export const SubscriptionDemand = () => {
  const dispatch = useDispatch();
  const rows = useSelector(selectSubscriptionRows);
  const { refreshedAt, status, error } = useSelector((state) => state.subscription);

  const [activeTab, setActiveTab] = useState('mainboard');
  const [searchFilter, setSearchFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Keep showing loaded rows while a refresh is in flight or after it fails
  const hasRows = rows.length > 0;
  const isLoading = !hasRows && (status === 'idle' || status === 'loading');
  const isFailed = !hasRows && status === 'failed';

  useEffect(() => {
    dispatch(fetchSubscriptionSummary({ maxAgeMs: 60000 }));
  }, [dispatch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchFilter]);

  const boardCounts = useMemo(
    () => rows.reduce((acc, row) => ({ ...acc, [row.board]: (acc[row.board] || 0) + 1 }), {}),
    [rows]
  );

  const filteredRows = useMemo(() => {
    const query = searchFilter.trim().toLowerCase();
    return rows.filter((row) => {
      if (row.board !== activeTab) return false;
      if (query && !row.company.toLowerCase().includes(query) && !row.symbol.toLowerCase().includes(query)) return false;
      return true;
    });
  }, [rows, activeTab, searchFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const page = Math.min(currentPage, totalPages);
  const pageRows = filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleRefresh = () => dispatch(fetchSubscriptionSummary());

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages || newPage === page) return;
    setCurrentPage(newPage);
    document.getElementById('subscription-demand')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const refreshedLabel = formatRefreshedAt(refreshedAt);

  return (
    <section id="subscription-demand" className="py-12 sm:py-16 bg-slate-50/50 border-t border-slate-200/80">
      <div className="w-full max-w-[1680px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-28">

        {/* Section Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 sm:gap-6">
          <div>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold mb-2">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span>Real-Time Institutional Bidding (QIB & NII)</span>
            </span>
            <div className="flex items-center gap-3">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                Live IPO Subscription Demand
              </h2>
              <span className="text-xs bg-indigo-50 border border-indigo-200 text-indigo-700 font-semibold px-2 py-0.5 rounded hidden sm:inline-block">
                NSE/BSE Broadcast
              </span>
            </div>
            <p className="text-slate-500 text-xs sm:text-sm mt-1 max-w-3xl">
              Track live institutional demand across QIB, NII (sNII, bNII) and Retail quotas. Real-time cumulative demand meter and subscription multiples record.
            </p>
            {refreshedLabel && (
              <p className="text-[11px] text-slate-400 mt-1">Updated {refreshedLabel} · Source: NSE</p>
            )}
          </div>

          {/* Tab Switcher, Search & Refresh */}
          <div className="flex flex-wrap items-center gap-2.5">
            {/* Mainboard vs SME Switcher */}
            <div className="bg-slate-200/70 p-1 rounded-xl flex items-center gap-1 text-xs">
              {BOARD_TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-3 py-1.5 rounded-lg font-semibold transition-all ${
                    activeTab === tab.key
                      ? 'bg-white text-indigo-700 shadow-xs'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {tab.label} ({boardCounts[tab.key] || 0})
                </button>
              ))}
            </div>

            {/* Quick Search */}
            <div className="relative w-44 sm:w-52">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                placeholder="Search company..."
                className="w-full pl-8 pr-3 py-1.5 text-xs bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <button
              type="button"
              onClick={handleRefresh}
              disabled={status === 'loading'}
              className="inline-flex items-center justify-center w-8 h-8 rounded-xl border border-slate-200 bg-white text-slate-600 hover:text-indigo-700 hover:border-indigo-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-xs flex-shrink-0"
              aria-label="Refresh subscription data"
              title="Refresh subscription data"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${status === 'loading' ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* 3 Callout Cards */}
        <SubscriptionHighlight rows={rows} isLoading={isLoading} />

        {/* Full Subscription Breakdown Table */}
        <SubscriptionTable
          rows={pageRows}
          totalCount={filteredRows.length}
          boardTotal={boardCounts[activeTab] || 0}
          page={page}
          pageSize={PAGE_SIZE}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          isLoading={isLoading}
          isFailed={isFailed}
          error={error}
          onRetry={handleRefresh}
          onClearSearch={() => setSearchFilter('')}
        />

        {/* Educational Guide Cards */}
        <SubscriptionInsights />

      </div>
    </section>
  );
};

export default SubscriptionDemand;
